import { CsvButton } from "@/components/CsvButton";
import { downloadCsv, today } from "@/lib/csv";
import React from "react";
import axios from "axios";
import { Loader2, ShieldCheck, Grid3x3, Flame, Crosshair } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// index 0 = 1 game ago (hottest) → index 19 = 20 games ago (coolest)
const AGO_COLORS = [
  "#f43f5e", "#fb7185", "#f97316", "#fb923c", "#f59e0b",
  "#facc15", "#a3e635", "#4ade80", "#34d399", "#2dd4bf",
  "#22d3ee", "#38bdf8", "#60a5fa", "#818cf8", "#a78bfa",
  "#c084fc", "#e879f9", "#94a3b8", "#64748b", "#475569",
];
const agoColor = (n) => AGO_COLORS[Math.min(n, 20) - 1];

const Badge = ({ children, className }) => (
  <span className={`ml-0.5 rounded px-[3px] text-[8px] font-bold leading-[12px] ${className}`}>{children}</span>
);

const Chip = ({ c, ago, testId, hot }) => (
  <div
    data-testid={testId}
    data-hot={hot ? "true" : "false"}
    title={`${c.player} #${c.jersey}${c.slot ? ` · ${c.slot}` : ""} · ${c.count} G · ${c.times.join(", ")}${c.first ? " · FIRST goal" : ""}${c.last ? " · LAST goal" : ""}`}
    className={`flex items-center whitespace-nowrap rounded-md px-1 py-0.5 font-mono text-[10px] font-bold text-slate-950 transition-transform duration-150 hover:scale-110 ${hot ? "ring-2 ring-white shadow-[0_0_10px_rgba(255,255,255,0.7)]" : "opacity-60"}`}
    style={{ backgroundColor: agoColor(ago) }}
  >
    #{c.jersey}
    {c.slot && <span className="ml-0.5 text-[8px] opacity-70">·{c.slot.replace(/\D/g, "")}</span>}
    {c.first && <Badge className="bg-rose-700 text-white">F</Badge>}
    {c.last && <Badge className="bg-emerald-700 text-white">L</Badge>}
    {c.count >= 3 ? <Badge className="bg-sky-800 text-white">x3</Badge> : c.count === 2 && <Badge className="bg-orange-700 text-white">2X</Badge>}
  </div>
);

const LegendBar = () => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 font-mono text-[9px] uppercase tracking-wider text-slate-400" data-testid="origin-chart-legend">
    <div className="flex items-center gap-1">
      <span>1 game ago</span>
      <div className="flex overflow-hidden rounded">
        {AGO_COLORS.map((c, i) => <span key={c} title={`${i + 1} games ago`} className="h-2.5 w-2.5" style={{ backgroundColor: c }} />)}
      </div>
      <span>20 ago</span>
    </div>
    <span><Badge className="bg-rose-700 text-white">F</Badge> First goal given up</span>
    <span><Badge className="bg-emerald-700 text-white">L</Badge> Last goal given up</span>
    <span><Badge className="bg-orange-700 text-white">2X</Badge> 2+ goals</span>
    <span><Badge className="bg-sky-800 text-white">x3</Badge> Hat trick</span>
    <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-cyan-400" /> Shutout</span>
    <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded ring-2 ring-white" /> Hot position vs tonight's opponent</span>
    <span className="flex items-center gap-1"><Flame className="h-3 w-3 text-orange-400" /> 3+ game streak</span>
  </div>
);

const streakCells = (cols, p) => {
  const cells = new Set();
  let longest = 0, active = 0, run = [];
  const flush = () => {
    if (run.length >= 3) run.forEach((a) => cells.add(a));
    longest = Math.max(longest, run.length);
    run = [];
  };
  cols.forEach((g) => (g.by_pos[p].length ? run.push(g.games_ago) : flush()));
  if (run.length && run[run.length - 1] === 1) active = run.length;
  flush();
  return { cells, longest, active: active >= 3 ? active : 0 };
};

const hotFrom = (side) => {
  const top = [...(side?.targets || [])].sort((a, b) => b.score - a.score).slice(0, 3);
  const hot = {};
  top.forEach((t) => { (hot[t.position] = hot[t.position] || []).push(t); });
  return hot;
};

const Stat = ({ label, value, testId, accent }) => (
  <div className="rounded border border-slate-800 bg-slate-950/60 px-2 py-1" data-testid={testId}>
    <p className="text-[8px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`text-[11px] font-bold ${accent || "text-slate-200"}`}>{value}</p>
  </div>
);

const Summary = ({ team, cols, positions, totals, streaks, hot }) => {
  const sl = {};
  const posL5 = Object.fromEntries(positions.map((p) => [p, 0]));
  cols.forEach((g) => positions.forEach((p) => g.by_pos[p].forEach((c) => {
    const k = c.slot || `${p}?`;
    const r = (sl[k] = sl[k] || { pos: p, l20: 0, l5: 0, first: 0, last: 0, multi: 0, hat: 0 });
    r.l20 += c.count;
    if (g.games_ago <= 5) { r.l5 += c.count; posL5[p] += c.count; }
    if (c.first) r.first += 1;
    if (c.last) r.last += 1;
    if (c.count >= 2) r.multi += 1;
    if (c.count >= 3) r.hat += 1;
  })));
  const keys = Object.keys(sl);
  const lead = (k, pos) => {
    const ks = keys.filter((x) => (!pos || sl[x].pos === pos) && sl[x][k] > 0).sort((a, b) => sl[b][k] - sl[a][k] || sl[b].l20 - sl[a].l20);
    return ks.length ? `${ks[0]} (${sl[ks[0]][k]})` : "—";
  };
  const sum = (k) => keys.reduce((x, s) => x + sl[s][k], 0);
  const shutouts = cols.filter((g) => g.shutout).length;
  const active = positions.filter((p) => streaks[p].active).map((p) => `${p} ${streaks[p].active}G`);
  const hotSlots = new Set(Object.values(hot).flat().map((t) => t.slot));
  const rate = (k) => sl[k].l5 * 2 + sl[k].l20 / 4 + sl[k].first + (streaks[sl[k].pos].active ? 3 : 0) + (hotSlots.has(k) ? 3 : hot[sl[k].pos] ? 1 : 0);
  const verdict = keys.filter((k) => !k.endsWith("?")).sort((a, b) => rate(b) - rate(a)).slice(0, 2);
  return (
    <div className="mt-2 rounded-lg border border-cyan-900/40 bg-slate-900/50 p-2 font-mono" data-testid={`origin-summary-${team}`}>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
        {positions.map((p) => (
          <Stat key={p} label={`${p} · L${cols.length} / L5 · top line`} value={`${totals[p]} / ${posL5[p]} · ${lead("l20", p)}`} testId={`origin-summary-${team}-${p}`} accent={verdict.some((k) => sl[k].pos === p) ? "text-rose-300" : undefined} />
        ))}
      </div>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5 sm:grid-cols-6">
        <Stat label="Most first goals" value={lead("first")} testId={`origin-summary-${team}-first`} accent="text-rose-300" />
        <Stat label="Most last goals" value={lead("last")} testId={`origin-summary-${team}-last`} accent="text-emerald-300" />
        <Stat label="2X games" value={`${sum("multi")} · ${lead("multi")}`} testId={`origin-summary-${team}-multi`} accent="text-orange-300" />
        <Stat label="Hat tricks" value={sum("hat") ? `${sum("hat")} · ${lead("hat")}` : 0} testId={`origin-summary-${team}-hat`} accent="text-sky-300" />
        <Stat label="Shutouts" value={shutouts} testId={`origin-summary-${team}-shutouts`} accent="text-cyan-300" />
        <Stat label="Active streaks" value={active.length ? active.join(", ") : "None"} testId={`origin-summary-${team}-streaks`} accent="text-orange-300" />
      </div>
      <p className="mt-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-rose-300" data-testid={`origin-verdict-${team}`}>
        <Crosshair className="h-3.5 w-3.5" /> Verdict: Attack {verdict.join(" / ") || "—"} tonight
      </p>
    </div>
  );
};

const TeamGrid = ({ data, oppSide, range = 20 }) => {
  if (!data?.ok) {
    return <p className="py-3 text-center font-mono text-[11px] text-slate-600">Origin chart unavailable for {data?.team || "team"}</p>;
  }
  const cols = data.games.filter((g) => g.games_ago <= range).sort((a, b) => b.games_ago - a.games_ago);
  const hot = hotFrom(oppSide);
  const streaks = Object.fromEntries(data.positions.map((p) => [p, streakCells(cols, p)]));
  const totals = Object.fromEntries(data.positions.map((p) => [p, cols.reduce((s, g) => s + g.by_pos[p].reduce((x, c) => x + c.count, 0), 0)]));
  return (
    <div data-testid={`origin-chart-${data.team}`}>
      <p className="mb-1.5 font-display text-[12px] font-bold uppercase tracking-wide text-slate-200">
        {data.team} defense <span className="font-mono text-[9px] font-normal text-slate-500">· goals allowed by position · last {cols.length}</span>
      </p>
      {oppSide?.attacker && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-slate-400" data-testid={`origin-hot-${data.team}`}>
          <Crosshair className="h-3 w-3 text-rose-400" /> vs {oppSide.attacker} tonight · hot:
          {Object.keys(hot).length ? Object.entries(hot).map(([p, ts]) => (
            <span key={p} className="rounded border border-rose-800/60 bg-rose-950/40 px-1.5 py-0.5 text-rose-200" data-testid={`origin-hot-pos-${data.team}-${p}`}>
              {p} · {ts.map((t) => `${t.jersey != null ? `#${t.jersey} ` : ""}${t.player.split(" ").slice(-1)[0]}`).join(", ")}
            </span>
          )) : <span className="text-slate-600">no confirmed targets</span>}
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full border-collapse font-mono text-[9px]">
          <thead>
            <tr className="bg-slate-900/80 text-slate-500">
              <th className="sticky left-0 z-10 bg-slate-900 px-2 py-1 text-left">POS</th>
              {cols.map((g) => (
                <th key={g.game_id} className="min-w-[36px] px-0.5 py-1 text-center" title={`${g.date} ${g.home_away === "H" ? "vs" : "@"} ${g.opponent} · ${g.final}`}>
                  <div className="mx-auto mb-0.5 h-1 w-6 rounded" style={{ backgroundColor: agoColor(g.games_ago) }} />
                  <div className="text-slate-300">{g.games_ago}</div>
                  <div className="text-[8px] text-slate-600">{g.home_away === "H" ? "v" : "@"}{g.opponent}</div>
                  <div className="whitespace-nowrap text-[8px] text-slate-400" data-testid={`origin-date-${data.team}-${g.games_ago}`}>{g.date ? `${g.date.slice(5, 7)}/${g.date.slice(8, 10)}${g.date.slice(0, 4) !== String(new Date().getFullYear()) ? `/${g.date.slice(2, 4)}` : ""}` : ""}</div>
                </th>
              ))}
              <th className="px-2 py-1 text-center text-cyan-400">L{cols.length}</th>
            </tr>
          </thead>
          <tbody>
            {data.positions.map((p) => (
              <tr key={p} className="border-t border-slate-800/60">
                <td className={`sticky left-0 z-10 bg-slate-950 px-2 py-1 font-bold ${hot[p] ? "text-rose-300" : "text-slate-300"}`}>
                  <div className="flex items-center gap-1">
                    {p}
                    {streaks[p].longest >= 3 && (
                      <span
                        data-testid={`origin-streak-${data.team}-${p}`}
                        title={`Longest streak ${streaks[p].longest} straight games${streaks[p].active ? ` · ACTIVE ${streaks[p].active}` : ""}`}
                        className={`flex items-center rounded px-0.5 text-[8px] ${streaks[p].active ? "animate-pulse bg-orange-600 text-white" : "bg-orange-950 text-orange-300"}`}
                      >
                        <Flame className="h-2.5 w-2.5" />{streaks[p].active || streaks[p].longest}
                      </span>
                    )}
                  </div>
                </td>
                {cols.map((g) => (
                  <td key={g.game_id} data-streak={streaks[p].cells.has(g.games_ago) ? "true" : "false"} className={`px-0.5 py-1 align-top ${g.shutout ? "bg-cyan-950/30" : ""} ${streaks[p].cells.has(g.games_ago) ? "bg-orange-500/10 shadow-[inset_0_1px_0_#f97316,inset_0_-1px_0_#f97316]" : ""}`}>
                    <div className="flex flex-col items-center gap-0.5">
                      {g.shutout && p === "C" && <ShieldCheck className="h-4 w-4 text-cyan-400" data-testid={`origin-shutout-${data.team}-${g.games_ago}`} />}
                      {g.by_pos[p].map((c) => (
                        <Chip key={`${c.jersey}-${c.player}`} c={c} ago={g.games_ago} testId={`origin-chip-${data.team}-${g.games_ago}-${p}-${c.jersey}`} hot={!oppSide || !!hot[p]} />
                      ))}
                    </div>
                  </td>
                ))}
                <td className="px-2 py-1 text-center font-bold text-cyan-300" data-testid={`origin-total-${data.team}-${p}`}>{totals[p]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Summary team={data.team} cols={cols} positions={data.positions} totals={totals} streaks={streaks} hot={hot} />
    </div>
  );
};

export const GoalOriginChart = ({ awayAbbr, homeAbbr, cypher }) => {
  const [data, setData] = React.useState(null);
  const [range, setRange] = React.useState(20);
  const [err, setErr] = React.useState(false);
  const load = React.useCallback(async () => {
    setErr(false);
    setData(null);
    try {
      const res = await axios.get(`${API}/origin-chart`, { params: { away: awayAbbr, home: homeAbbr, games: 20 } });
      setData(res.data);
    } catch {
      setErr(true);
    }
  }, [awayAbbr, homeAbbr]);
  React.useEffect(() => { load(); }, [load]);

  return (
    <section className="space-y-3 rounded-xl border border-slate-800 bg-slate-900/30 p-3" data-testid={`goal-origin-chart-${awayAbbr}-${homeAbbr}`}>
      <div className="flex items-center gap-2">
        <Grid3x3 className="h-4 w-4 text-cyan-400" />
        <p className="font-display text-[13px] font-bold uppercase tracking-wide text-cyan-300">Goal Origin Chart · Last {range}</p>
        <div className="ml-auto">
          <CsvButton label="Chart CSV" testId={`csv-origin-${awayAbbr}-${homeAbbr}`} onClick={() => downloadCsv(
            `goal-origin-chart_L${range}_${awayAbbr}-at-${homeAbbr}_${today()}.csv`,
            [data?.away, data?.home].filter((t) => t?.ok).flatMap((t) => t.games.filter((g) => g.games_ago <= range).flatMap((g) => {
              const base = { defense: t.team, games_ago: g.games_ago, date: g.date, opponent: g.opponent, home_away: g.home_away, final: g.final, shutout: g.shutout ? "YES" : "" };
              const chips = t.positions.flatMap((p) => g.by_pos[p].map((c) => ({ ...base, position: p, jersey: c.jersey, player: c.player, line: c.slot, goals: c.count, first_goal: c.first ? "YES" : "", last_goal: c.last ? "YES" : "", times: (c.times || []).join(" ") })));
              return chips.length ? chips : [base];
            })))} />
        </div>
        <div className="flex rounded-md border border-slate-800 bg-slate-950 p-0.5" data-testid="origin-range-toggle">
          {[5, 10, 20].map((n) => (
            <button key={n} type="button" data-testid={`origin-range-${n}`} onClick={() => setRange(n)}
              className={`min-w-[44px] rounded px-2.5 py-1.5 font-mono text-[11px] font-bold transition-colors ${range === n ? "bg-cyan-500/20 text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}>
              L{n}
            </button>
          ))}
        </div>
      </div>
      <LegendBar />
      {err ? (
        <div className="py-4 text-center">
          <p className="font-mono text-[11px] text-rose-400">Origin chart failed to load</p>
          <button onClick={load} data-testid="origin-chart-retry" className="mt-2 rounded bg-slate-800 px-3 py-1 font-mono text-[11px] text-slate-300 hover:bg-slate-700">Retry</button>
        </div>
      ) : !data ? (
        <div className="flex items-center justify-center gap-2 py-6 font-mono text-[11px] text-slate-500" data-testid="origin-chart-loading">
          <Loader2 className="h-4 w-4 animate-spin text-cyan-500" /> Building 20-game origin chart…
        </div>
      ) : (
        <>
          <TeamGrid data={data.away} oppSide={[cypher?.away, cypher?.home].find((s) => s?.defender === awayAbbr)} range={range} />
          <TeamGrid data={data.home} oppSide={[cypher?.away, cypher?.home].find((s) => s?.defender === homeAbbr)} range={range} />
        </>
      )}
    </section>
  );
};

export default GoalOriginChart;
